import React, { Fragment } from "react";

import clsx from "clsx";
import { compact } from "lodash";

import { ControlBar } from "@components/control-bar";
import { StuckToTop } from "@components/stuck-to-top";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { useLogger } from "@components/v2/analytics";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { useNorthbeamQuery } from "@hooks/http";
import { useOrderQueriesLoading } from "@hooks/use-order-page-loading";
import { prettyLogEvent } from "@internal/analytics";
import { type FetchOrderPlatformAndEcommerceOptions } from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";
import {
  CompareDateRangeEnum,
  DateRangeChoiceEnum,
  EcomPlatformFriendlyNames,
  PlatformFriendlyNames,
  TimeGranularityV2,
} from "@north-beam/nb-common";
import {
  useOrderPageAdPlatforms,
  useOrderPageAdjustForReturns,
  useOrderPageEcommercePlatforms,
} from "@pages/orders/use-orders-filters";
import { FETCH_ORDER_PLATFORMS_AND_ECOMMERCE_OPTIONS } from "@pages/settings/queries";
import { formatDateRangeText } from "@shared/date-range-picker";
import { DateRangeSelect } from "@shared/selects";
import { CompareDateRangeSelect } from "@shared/selects/compare-date-range-select";
import { TimeGranularityV2Select } from "@shared/selects/time-granularity-v2-select";
import { renderChevronAnimation } from "@shared/selects/utils";
import { reasonableGuessAtNaming } from "@utils/pretty-names";

import { useReportBodyState } from "./orders-control-state";
import { MultiBadgeEditor } from "./orders-table/editors";

export interface ReportBodyControlProps {
  isLoading: boolean;
}

type SelectorProps = {
  isLoading: boolean;
  options: string[];
};

const AdPlatformSelector = ({ isLoading, options }: SelectorProps) => {
  const [adPlatforms, setAdPlatforms] = useOrderPageAdPlatforms();
  const orderQueriesLoading = useOrderQueriesLoading();
  const { user } = useUser();
  const logger = useLogger({ container: "top-bar" });

  return (
    <div className="w-full">
      <div>
        <label>Ad Platforms</label>
        <GenericTooltip
          content={
            <>
              An ad platform represents the platform used as part of a
              touchpoint in the customer journey
            </>
          }
        />
      </div>
      <MultiBadgeEditor
        type="ad-platform"
        isClearable
        isSearchable
        shouldAnon={false}
        placeholder="Select ad platforms..."
        options={options.map((o) => ({
          value: o,
          label: PlatformFriendlyNames[o] || reasonableGuessAtNaming(o),
        }))}
        isLoading={isLoading || orderQueriesLoading}
        onChange={(platforms) => {
          prettyLogEvent.onSelect({
            user,
            page: Pages.ORDERS,
            additionalActionInfo: "ad-platform",
            data: { platforms },
          });
          logger.event.ordersPageAdPlatformSelectEvent("click", {
            eventDetails: {
              platforms: platforms ?? [],
            },
          });
          setAdPlatforms(platforms);
        }}
        value={compact(adPlatforms)}
      />
    </div>
  );
};

const EcommercePlatformSelector = ({ isLoading, options }: SelectorProps) => {
  const [ecommercePlatforms, setEcommercePlatforms] =
    useOrderPageEcommercePlatforms();
  const orderQueriesLoading = useOrderQueriesLoading();
  const { user } = useUser();

  return (
    <div className="w-full">
      <div>
        <label>E-commerce Platforms</label>
        <GenericTooltip
          content={
            <>
              An e-commerce platform is the platform responsible for managing
              the order
            </>
          }
        />
      </div>
      <MultiBadgeEditor
        type="ecommerce-platform"
        isClearable
        isSearchable
        shouldAnon={false}
        placeholder="Select e-commerce platforms..."
        options={options.map((o) => ({
          value: o,
          label: EcomPlatformFriendlyNames[o] || reasonableGuessAtNaming(o),
        }))}
        isLoading={isLoading || orderQueriesLoading}
        onChange={(platforms) => {
          prettyLogEvent.onSelect({
            user,
            page: Pages.ORDERS,
            additionalActionInfo: "e-commerce-platform",
            data: { platforms },
          });
          setEcommercePlatforms(platforms);
        }}
        value={compact(ecommercePlatforms)}
      />
    </div>
  );
};

export const OrdersControl = ({ isLoading }: ReportBodyControlProps) => {
  const {
    state,
    timeGranularity,
    setTimeGranularity,
    setDateRange,
    setCompareDateRange,
  } = useReportBodyState();
  const { user } = useUser();

  const [adjustForReturns, setAdjustForReturns] =
    useOrderPageAdjustForReturns();

  const orderQueriesLoading = useOrderQueriesLoading();

  let maxLookbackDays = 20 * 365;
  let maxLookbackDaysCompare = 20 * 365;

  if (timeGranularity === "hourly") {
    maxLookbackDays = 8;
    maxLookbackDaysCompare = 15;
  }

  const dateRangeCurrent =
    typeof state.dateRange === "string"
      ? DateRangeChoiceEnum.toReactSelect(state.dateRange)
      : state.dateRange;

  const compareDateRangeCurrent =
    typeof state.compareDateRange === "string"
      ? CompareDateRangeEnum.toReactSelect(state.compareDateRange)
      : state.compareDateRange;

  const { data: optionsData, loading } = useNorthbeamQuery<
    FetchOrderPlatformAndEcommerceOptions,
    unknown
  >(FETCH_ORDER_PLATFORMS_AND_ECOMMERCE_OPTIONS);

  return (
    <StuckToTop>
      {() => (
        <ControlBar className="border">
          <div className="flex justify-center">
            <div className="flex w-[36rem]">
              <div className="flex-1">
                <EcommercePlatformSelector
                  isLoading={loading}
                  options={optionsData?.me.ecommercePlatforms || []}
                />
              </div>
              <div className="mr-4" />
              <div className="flex-1 max-w-[280px]">
                <AdPlatformSelector
                  isLoading={loading}
                  options={optionsData?.me.adPlatforms || []}
                />
              </div>
            </div>
            <div className="flex">
              <label className="container justify-center mt-9">
                <input
                  type="checkbox"
                  checked={adjustForReturns}
                  onChange={() => {
                    prettyLogEvent.onSelect({
                      user,
                      page: Pages.ORDERS,
                      additionalActionInfo: "adjust-for-returns",
                      data: {
                        adjustForReturns: !adjustForReturns,
                      },
                    });
                    setAdjustForReturns(!adjustForReturns);
                  }}
                  className="mr-2"
                  disabled={orderQueriesLoading}
                />
                Adjust for returns
              </label>
            </div>
          </div>
          <div className="flex flex-row items-center justify-content-end pl-5 ml-auto">
            <Popover className="nb-input-container relative">
              <PopoverButton
                className="nb-input flex flex-col items-start mr-2 text-sm w-64 h-14"
                disabled={isLoading}
              >
                {({ open }) => (
                  <div className="flex items-center space-between w-full">
                    <div className="flex-grow text-left">
                      <div>
                        {formatDateRangeText(dateRangeCurrent)},{" "}
                        {TimeGranularityV2.getLabel(timeGranularity)}
                      </div>
                      <small className="text-muted mt-1 text-small">
                        Compared to{" "}
                        {formatDateRangeText(compareDateRangeCurrent)}
                      </small>
                    </div>
                    {renderChevronAnimation(open)}
                  </div>
                )}
              </PopoverButton>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <PopoverPanel
                  className={clsx(
                    "absolute right-0 z-100 mt-2 w-72 elevation-1",
                    "rounded-md p-3 bg-white flex flex-col space-y-2",
                  )}
                >
                  <TimeGranularityV2Select
                    isLoading={isLoading}
                    timeGranularity={timeGranularity}
                    onChange={(granularity) => {
                      prettyLogEvent.onSelect({
                        user,
                        page: Pages.ORDERS,
                        additionalActionInfo: "granularity",
                        data: {
                          granularity,
                        },
                      });
                      setTimeGranularity(granularity);
                    }}
                  />
                  <DateRangeSelect
                    isLoading={isLoading}
                    maxLookbackDays={maxLookbackDays}
                    popoverPlacement="bottom-end"
                    dateRange={state.dateRange}
                    onUpdate={(dateRange) => {
                      prettyLogEvent.onSelect({
                        user,
                        page: Pages.ORDERS,
                        additionalActionInfo: "date-range",
                        data: {
                          dateRange,
                        },
                      });
                      setDateRange(dateRange);
                    }}
                  />
                  <CompareDateRangeSelect
                    isLoading={isLoading}
                    dateRange={state.dateRange}
                    compareDateRange={state.compareDateRange}
                    onUpdate={(dateRange) => {
                      prettyLogEvent.onSelect({
                        user,
                        page: Pages.ORDERS,
                        additionalActionInfo: "compare-date-range",
                        data: {
                          dateRange,
                        },
                      });
                      setCompareDateRange(dateRange);
                    }}
                    maxLookbackDays={maxLookbackDaysCompare}
                  />
                </PopoverPanel>
              </Transition>
            </Popover>
          </div>
        </ControlBar>
      )}
    </StuckToTop>
  );
};
